import { useCallback, useEffect, useState } from 'react';
import {
  consolidateClosureEmployees,
  listClosureEmployees,
  getPayrollClosure,
} from '../api/liquidacionApi';

function extractPayload(res) {
  if (!res) return null;
  return res.data !== undefined ? res.data : res;
}

function errorToMessage(err) {
  return (
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.message ||
    'Error al cargar datos'
  );
}

export function useClosurePayrollData(closureId) {
  const [closure, setClosure] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadInitialData = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setError('');

    try {
      const closureRes = await getPayrollClosure(id);
      const closureData = extractPayload(closureRes);
      setClosure(closureData);

      const listRes = await listClosureEmployees(id);
      let body = extractPayload(listRes);
      let employeesArray = Array.isArray(body?.data)
        ? body.data
        : (Array.isArray(body) ? body : []);

      // Si es un cierre nuevo (DRAFT) y no tiene empleados consolidados aún,
      // forzamos la consolidación para traer los seleccionados con balances en 0 o calculados ahora.
      if (employeesArray.length === 0) {
        const consolidateRes = await consolidateClosureEmployees(id);
        body = extractPayload(consolidateRes);
        employeesArray = Array.isArray(body?.data)
          ? body.data
          : (Array.isArray(body) ? body : []);
      }

      setEmployees(employeesArray);
      setMeta(body?.meta ?? null);
    } catch (err) {
      setClosure(null);
      setEmployees([]);
      setError(errorToMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const forceConsolidate = useCallback(async () => {
    if (!closureId) return;
    setLoading(true);
    try {
      const consolidateRes = await consolidateClosureEmployees(closureId);
      const body = extractPayload(consolidateRes);
      const employeesArray = Array.isArray(body?.data) ? body.data : (Array.isArray(body) ? body : []);
      setEmployees(employeesArray);
      setMeta(body?.meta ?? null);
    } catch (err) {
      setError(errorToMessage(err));
    } finally {
      setLoading(false);
    }
  }, [closureId]);

  const refetchList = useCallback(async () => {
    if (!closureId) return;
    try {
      const res = await listClosureEmployees(closureId);
      const body = extractPayload(res);
      const employeesArray = Array.isArray(body?.data) ? body.data : (Array.isArray(body) ? body : []);
      setEmployees(employeesArray);
      setMeta(body?.meta ?? null);
    } catch (err) {
      setError(errorToMessage(err));
    }
  }, [closureId]);

  useEffect(() => {
    setClosure(null);
    setEmployees([]);
    setMeta(null);
    setError('');
    setLoading(false);

    if (closureId) {
      loadInitialData(closureId);
    }
    return () => {
      setEmployees([]);
      setClosure(null);
    };
  }, [closureId, loadInitialData]);

  return {
    closure,
    employees,
    meta,
    loading,
    error,
    refetch: forceConsolidate,
    refetchList,
  };
}